import { useEffect, useRef } from 'react'
import { useOptionalCommands } from '../../commands/CommandProvider.jsx'
import { moveChartViewport, releaseChartViewport } from '../../lib/chart/keyboardViewport.js'

export function useChartNavigationCommands({ active, containerRef, getChart, getLatestTime, ready, openSettings, toggleExecutionMarkers, onNavigate }) {
  const commands = useOptionalCommands()
  const ownedChart = useRef(null)
  useEffect(() => {
    const container = containerRef.current
    const cancel = () => {
      releaseChartViewport(ownedChart.current)
      ownedChart.current = null
    }
    // Mouse/touch navigation takes ownership before the native range changes.
    container?.addEventListener('pointerdown', cancel, true)
    container?.addEventListener('wheel', cancel, { capture: true, passive: true })
    return () => {
      container?.removeEventListener('pointerdown', cancel, true)
      container?.removeEventListener('wheel', cancel, true)
      cancel()
    }
  }, [active, ready, containerRef])
  useEffect(() => {
    if (!active || !commands) return undefined
    const available = () => !containerRef.current?.closest('[inert]')
    const dispose = [commands.registerCommandHandler('position.chart.settings.open', () => {
      if (!available()) return { ok: false, reason: 'chart_unavailable' }
      openSettings()
      return { ok: true }
    })]
    if (toggleExecutionMarkers) dispose.push(commands.registerCommandHandler('position.chart.execution_markers.toggle', () => {
      if (!available()) return { ok: false, reason: 'chart_unavailable' }
      toggleExecutionMarkers()
      return { ok: true }
    }))
    for (const [name, action] of [['zoom_in', 'in'], ['zoom_out', 'out'], ['scroll_left', 'left'], ['scroll_right', 'right']]) {
      dispose.push(commands.registerCommandHandler(`position.chart.${name}`, () => {
        if (!available() || !ready) return { ok: false, reason: 'chart_unavailable' }
        try {
          const chart = getChart()
          onNavigate?.()
          if (ownedChart.current !== chart) {
            releaseChartViewport(ownedChart.current)
            ownedChart.current = chart
          }
          return moveChartViewport(chart, action, getLatestTime())
        } catch {
          return { ok: false, reason: 'chart_unavailable' }
        }
      }))
    }
    return () => dispose.forEach(unregister => unregister())
  })
}
